import { createFocusTrap } from "focus-trap";
import { add, listen, qs, remove } from "@fluorescent/dom";
import { disableBodyScroll, enableBodyScroll } from "body-scroll-lock";
import { makeRequest } from "@/lib/xhr";
import dispatchCustomEvent from "@/lib/dispatch-custom-event";

import { emit, on } from "@/glow/events";
import { default as Product } from "@/lib/product/product";
import { animateQuickProduct, shouldAnimate } from "@/lib/animation";

const classes = {
  visible: "is-visible",
  active: "active",
  hidden: "hidden",
  fixed: "is-fixed",
  empty: "empty",
  animationProductRevealed: "animation--quick-product-revealed",
};

const selectors = {
  closeBtn: "[data-modal-close]",
  wash: ".modal__wash",
  modalContainer: "[data-quick-product-modal]",
  modalContent: ".quick-product-modal__content",
  modalTemplate: "#quick-product-modal-template",
  modalContentLeft: ".quick-product__left",
  modalContentRight: ".quick-product__right-inner",
  modalContentTitle: ".quick-product__right-inner .product__title",
  modalContentLoader: ".quick-product-modal-loading-indicator",
  quickHeaderWrapper: ".product__block--product-header",
  quickHeader:
    ".product__block--product-header-image-wrap + .product__block--product-header-inner",
  quickStickyHeader: "[data-quick-sticky-header]",
  quickImage: ".product__block--product-header-image-wrap",
};

const quickProductModal = node => {
  const focusTrap = createFocusTrap(node, { allowOutsideClick: true });
  const wash = qs(selectors.wash, node);
  const closeButton = qs(selectors.closeBtn, node);
  const modalContent = qs(selectors.modalContent, node);
  const modalTemplate = qs(selectors.modalTemplate, node).content;
  const isTemplate =
    window.location.href.includes("view=quick-view") ||
    window.location.href.includes("view=quick-add");
  const isEditorTemplate = window.Shopify.designMode && isTemplate;

  let quickProductAnimation = null;
  let product;

  if (shouldAnimate(node)) {
    quickProductAnimation = animateQuickProduct(node);
  }

  modalContent.append(modalTemplate.cloneNode(true));

  const events = [
    listen([wash, closeButton], "click", e => {
      e.preventDefault();

      if (!isEditorTemplate) _close();
    }),
    listen(node, "keydown", ({ keyCode }) => {
      if (keyCode === 27 && !isEditorTemplate) _close();
    }),
    on(["quick-add:open", "quick-view:open"], (state, data) => {
      node.dataset.quickProductType = data.type;
      if (data.productMedia || data.productBadges || data.productTitle) {
        _renderPlaceholderContent(data);
      }

      _renderProductContent(data);
      _open();
    }),
    on(["quick-add:close", "quick-view:close"], () => {
      if (!isEditorTemplate) _close();
    }),
  ];

  const _renderPlaceholderContent = data => {
    const modalContentLeft = qs(selectors.modalContentLeft, modalContent);
    const modalContentRight = qs(selectors.modalContentRight, modalContent);
    const modalContentTitle = qs(selectors.modalContentTitle, modalContent);
    const modalContentLoader = qs(selectors.modalContentLoader, modalContent);

    remove(modalContent, classes.empty);
    remove(modalContentLoader, classes.hidden);

    if (data.type === "quick-add") {
      modalContentLeft.remove();
    } else if (data.productMedia) {
      modalContentLeft.append(data.productMedia.cloneNode(true));
    }

    modalContentTitle.innerText = data.productTitle;

    if (data.productBadges) {
      modalContentRight.prepend(data.productBadges.cloneNode(true));
    }
  };

  const _renderProductContent = data => {
    const urlParamDelimiter = data.productUrl.includes("?") ? "&" : "?";
    const xhrUrl = `${data.productUrl}${urlParamDelimiter}view=${data.type}`;

    makeRequest("GET", xhrUrl).then(response => {
      let container = document.createElement("div");
      container.innerHTML = response;
      const productElement = qs("[data-quick-product]", container);

      remove(modalContent, classes.empty);
      modalContent.innerHTML = "";
      modalContent.appendChild(productElement);

      const renderedProductElement = qs("[data-quick-product]", modalContent);
      product = new Product(renderedProductElement);

      if (shouldAnimate(node)) {
        quickProductAnimation.animate();

        const modalContainer = modalContent.closest(selectors.modalContainer);

        setTimeout(() => {
          add(modalContainer, classes.animationProductRevealed);
        }, 100);
      }

      if (data.type === "quick-view") {
        dispatchCustomEvent("quick-view:loaded");
        _handleQuickViewStickyHeader();
      }
    });
  };

  const _handleQuickViewStickyHeader = () => {
    const quickStickyHeader = qs(selectors.quickStickyHeader, node);
    const quickHeaderWrapper = qs(selectors.quickHeaderWrapper, node);
    const quickHeader = qs(selectors.quickHeader, node);
    const quickImage = qs(selectors.quickImage, node);

    const observer = new IntersectionObserver(
      ([{ isIntersecting: visible }]) => {
        if (visible) {
          remove(quickStickyHeader, classes.active);
        } else {
          add(quickStickyHeader, classes.active);

          if (
            quickHeaderWrapper.dataset.headerPositionMobile === "below" &&
            quickImage.style.paddingTop === ""
          ) {
            quickImage.style.paddingTop = `${quickStickyHeader.offsetHeight}px`;
          }
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(quickHeader);
  };

  const _open = () => {
    add(node, classes.fixed);

    setTimeout(() => {
      add(node, classes.active);
      setTimeout(() => {
        add(node, classes.visible);
        focusTrap.activate();
      }, 50);
    }, 50);

    disableBodyScroll(node, {
      allowTouchMove: el => {
        while (el && el !== document.body) {
          if (el.getAttribute("data-scroll-lock-ignore") !== null) {
            return true;
          }
          el = el.parentNode;
        }
      },
      reserveScrollBarGap: true,
    });
  };

  const _close = () => {
    focusTrap.deactivate();
    remove(node, classes.visible);
    remove(node, classes.active);
    remove(node, classes.animationProductRevealed);
    enableBodyScroll(node);
    emit("quick-cart:scrollup");

    setTimeout(() => {
      remove(node, classes.fixed);

      modalContent.innerHTML = "";
      modalContent.append(modalTemplate.cloneNode(true));
      add(modalContent, classes.empty);
      product?.unload();
    }, 500);
  };

  const unload = () => {
    events.forEach(unsubscribe => unsubscribe());
  };

  return { unload };
};

export default quickProductModal;
